import { useEffect, useState, useCallback } from "react";
import useErroPadrao from "../../../hooks/useErroPadrao";
import useHelp from "../../../hooks/useHelp";
import useContabilidade from "../../../hooks/useContabilidade";
import { real, dataBR, hojeISO } from "../../../utils/formata";

// PAGAR EM DIÁRIAS (Dani, 19/09: "pagar diariamente, igual eu recebo"). Uma conta que a Help paga por dia vira
// N contas a pagar (uma por diária, com vencimento) — estrutura que o servidor já tem. Ao pagar, a diária vira
// despesa no Caixa/Dashboard (como a parcela recebida vira entrada) e a conta recebe a baixa. O que liga as
// diárias de uma mesma conta é a observação: "PAGAR EM DIÁRIAS · plano X · 3/20 · categoria Y · despesa #N".
const MARCA = "PAGAR EM DIÁRIAS";
const CATEGORIAS = {
    despesa: "Despesa geral", aluguel: "Aluguel", salarios: "Salários / Comissões", combustivel: "Combustível / Transporte",
    alimentacao: "Alimentação", material: "Material / Equipamento", marketing: "Marketing / Anúncios", impostos: "Impostos / Taxas", outro: "Outro",
};
// nome da categoria na Contabilidade (a baixa da conta gera o lançamento com ela)
const CATEGORIA_CONTABIL = {
    despesa: "Outros", aluguel: "Aluguel", salarios: "Salário", combustivel: "Combustível", alimentacao: "Alimentação",
    material: "Material de escritório", marketing: "Marketing", impostos: "Impostos", outro: "Outros",
};
const CHAVE_ABERTO = "despesas-diarias-aberto";
const barraLote = {
    position: "fixed", left: 16, right: 16, bottom: 16, maxWidth: 520, marginLeft: "auto", zIndex: 150,
    background: "#132436", border: "1px solid #d4af37", borderRadius: 16, padding: "12px 16px 14px",
    boxShadow: "0 14px 40px rgba(0, 0, 0, 0.55)",
};
const botaoCheio = { flex: "1 1 auto", minHeight: 48, fontWeight: 700, whiteSpace: "nowrap" };

const ler = (obs, re) => (String(obs || "").match(re) || [])[1];
function datasDasDiarias(inicio, quantidade, segSab) {
    const datas = [];
    const d = new Date(`${inicio}T12:00:00`);
    while (datas.length < quantidade) {
        if (!(segSab && d.getDay() === 0)) {
            datas.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
        }
        d.setDate(d.getDate() + 1);
    }
    return datas;
}
function agruparPlanos(contas) {
    const planos = new Map();
    for (const c of contas) {
        if (c.tipo !== "pagar" || !String(c.observacao || "").startsWith(MARCA)) continue;
        const plano = ler(c.observacao, /plano ([\w-]+)/);
        if (!plano) continue;
        const diaria = { ...c, n: Number(ler(c.observacao, /· (\d+)\/\d+/)) || 0, despesaId: Number(ler(c.observacao, /despesa #(\d+)/)) || null };
        if (!planos.has(plano)) {
            planos.set(plano, { plano, nome: c.pessoa || String(c.descricao).replace(/ — diária.*$/, ""), categoria: ler(c.observacao, /categoria (\w+)/) || "despesa", diarias: [] });
        }
        planos.get(plano).diarias.push(diaria);
    }
    return [...planos.values()].map((p) => {
        p.diarias.sort((a, b) => a.n - b.n || String(a.vencimento).localeCompare(String(b.vencimento)));
        p.abertas = p.diarias.filter((d) => d.status === "aberta");
        p.pagas = p.diarias.filter((d) => d.status === "paga");
        p.faltam = p.abertas.reduce((s, d) => s + Number(d.valor), 0);
        p.pago = p.pagas.reduce((s, d) => s + Number(d.valorPago ?? d.valor), 0);
        p.valorDiaria = Number(p.diarias[0]?.valor || 0);
        return p;
    }).sort((a, b) => (b.abertas.length > 0) - (a.abertas.length > 0) || String(a.abertas[0]?.vencimento || "").localeCompare(String(b.abertas[0]?.vencimento || "")));
}

export default function PagarEmDiarias({ onMudou }) {
    const [planos, setPlanos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [aberto, setAberto] = useState(() => { try { return localStorage.getItem(CHAVE_ABERTO) || ""; } catch { return ""; } });
    const [novo, setNovo] = useState(null);
    const [criando, setCriando] = useState(null); // { feitas, total }
    const [marcadas, setMarcadas] = useState([]);
    const [pagando, setPagando] = useState(null); // { ids, dataPagamento, formaPagamento, valor?, enviando, feitas }
    const [aviso, setAviso] = useState(null);
    const { adicionarDespesa, removerDespesa } = useHelp();
    const { listarContas, criarConta, atualizarConta, excluirConta, baixarConta, estornarConta } = useContabilidade();
    const { setErroPadrao } = useErroPadrao();
    const hoje = hojeISO();

    const carregar = useCallback(() => {
        listarContas({ tipo: "pagar" })
            .then((r) => setPlanos(agruparPlanos(r.contas || [])))
            .catch((error) => setErroPadrao(error))
            .finally(() => setCarregando(false));
    }, [listarContas, setErroPadrao]);
    useEffect(() => { carregar(); }, [carregar]);

    const alternarAberto = (plano) => {
        const v = aberto === plano ? "" : plano;
        setAberto(v);
        try { localStorage.setItem(CHAVE_ABERTO, v); } catch {}
    };
    const todasAbertas = planos.flatMap((p) => p.abertas.map((d) => ({ ...d, categoriaDespesa: p.categoria })));
    const paraHoje = todasAbertas.filter((d) => d.vencimento <= hoje);
    const atrasadas = todasAbertas.filter((d) => d.vencimento < hoje);
    const selecionadas = todasAbertas.filter((d) => marcadas.includes(d.id));
    const totalSelecionado = selecionadas.reduce((s, d) => s + Number(d.valor), 0);
    const alternarMarcada = (id) => setMarcadas((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

    // ---- nova conta em diárias
    const datasNovo = novo && Number(novo.quantidade) > 0 && novo.inicio ? datasDasDiarias(novo.inicio, Math.min(Number(novo.quantidade), 400), novo.dias === "seg-sab") : [];
    async function handleCriar(e) {
        e.preventDefault();
        const valor = Math.round(Number(novo.valor) * 100) / 100;
        const quantidade = Number(novo.quantidade);
        if (!(valor > 0) || !Number.isInteger(quantidade) || quantidade < 1 || quantidade > 400) return;
        const plano = Date.now().toString(36);
        const nome = String(novo.nome).trim();
        const datas = datasDasDiarias(novo.inicio, quantidade, novo.dias === "seg-sab");
        let feitas = 0;
        setCriando({ feitas: 0, total: quantidade });
        try {
            // de 4 em 4: 60 diárias saem em segundos sem afogar o servidor
            for (let i = 0; i < datas.length; i += 4) {
                await Promise.all(datas.slice(i, i + 4).map((vencimento, j) => criarConta({
                    tipo: "pagar",
                    descricao: `${nome} — diária ${i + j + 1}/${quantidade}`,
                    valor,
                    vencimento,
                    categoria: CATEGORIA_CONTABIL[novo.categoria] || "Outros",
                    pessoa: nome,
                    observacao: `${MARCA} · plano ${plano} · ${i + j + 1}/${quantidade} · categoria ${novo.categoria}`,
                })));
                feitas = Math.min(i + 4, datas.length);
                setCriando({ feitas, total: quantidade });
            }
            setNovo(null);
            setAberto(plano);
            try { localStorage.setItem(CHAVE_ABERTO, plano); } catch {}
            setAviso({ tipo: "ok", msg: `${nome}: ${quantidade} diárias de ${real(valor)} cadastradas (${dataBR(datas[0])} a ${dataBR(datas[datas.length - 1])}).` });
        } catch (error) {
            setAviso({ tipo: "erro", msg: `Parou no meio: ${feitas} de ${quantidade} diárias cadastradas. Confira abaixo e, se quiser, exclua e cadastre de novo.` });
            setErroPadrao(error);
        } finally {
            setCriando(null);
            carregar();
        }
    }

    // ---- pagar (uma ou várias): vira despesa no Caixa e baixa a conta; se a baixa falhar, a despesa é desfeita
    async function pagarUma(d, { dataPagamento, formaPagamento, valor }) {
        const r = await adicionarDespesa({ categoria: d.categoriaDespesa, valor, data: dataPagamento, descricao: d.descricao, formaPagamento });
        const despesaId = r?.despesa?.id;
        try {
            await baixarConta(d.id, { data: dataPagamento, valorPago: valor, formaPagamento });
        } catch (error) {
            if (despesaId) await removerDespesa(despesaId).catch(() => {});
            throw error;
        }
        if (despesaId) await atualizarConta(d.id, { observacao: `${d.observacao} · despesa #${despesaId}` }).catch(() => {});
    }
    async function handlePagar(e) {
        e.preventDefault();
        const lista = todasAbertas.filter((d) => pagando.ids.includes(d.id));
        const { dataPagamento, formaPagamento } = pagando;
        let feitas = 0;
        setPagando((p) => ({ ...p, enviando: true, feitas: 0 }));
        try {
            for (const d of lista) {
                const valor = lista.length === 1 && Number(pagando.valor) > 0 ? Number(pagando.valor) : Number(d.valor);
                await pagarUma(d, { dataPagamento, formaPagamento, valor });
                const ate = ++feitas;
                setPagando((p) => ({ ...p, feitas: ate }));
            }
            setMarcadas([]);
            setAviso({ tipo: "ok", msg: `${feitas} ${feitas === 1 ? "diária paga" : "diárias pagas"} — já entrou nas despesas e no Caixa.` });
        } catch (error) {
            setMarcadas(lista.slice(feitas).map((d) => d.id));
            setAviso({ tipo: "erro", msg: `Parou na diária ${lista[feitas]?.n}: ${feitas} de ${lista.length} pagas. As que faltam continuam marcadas.` });
            setErroPadrao(error);
        } finally {
            setPagando(null);
            carregar();
            if (onMudou) onMudou();
        }
    }

    async function handleEstornar(d) {
        if (!window.confirm(`Desfazer o pagamento da diária ${d.n} de ${d.pessoa || "—"}? A despesa sai do Caixa.`)) return;
        try {
            await estornarConta(d.id);
            if (d.despesaId) await removerDespesa(d.despesaId).catch(() => {});
            await atualizarConta(d.id, { observacao: String(d.observacao).replace(/ · despesa #\d+/, "") }).catch(() => {});
            setAviso({ tipo: "ok", msg: `Diária ${d.n} voltou a ficar em aberto.` });
        } catch (error) {
            setErroPadrao(error);
        } finally {
            carregar();
            if (onMudou) onMudou();
        }
    }

    async function handleExcluirPlano(p) {
        const msg = p.pagas.length
            ? `Excluir as ${p.abertas.length} diárias que faltam de ${p.nome}? As ${p.pagas.length} já pagas continuam nas despesas.`
            : `Excluir a conta de ${p.nome} (${p.diarias.length} diárias)?`;
        if (!window.confirm(msg)) return;
        try {
            for (const d of p.abertas) await excluirConta(d.id);
            setAviso({ tipo: "ok", msg: `${p.nome}: diárias em aberto excluídas.` });
        } catch (error) {
            setErroPadrao(error);
        } finally {
            carregar();
        }
    }

    // uma diária só (pelo botão da linha OU pela barra): o campo "Valor pago" já vem com o valor dela
    const abrirPagamento = (ids) => setPagando({ ids, dataPagamento: hoje, formaPagamento: "pix", valor: ids.length === 1 ? todasAbertas.find((d) => d.id === ids[0])?.valor : undefined });

    return (
        <div className={`he-card ${novo || pagando || selecionadas.length ? "sem-vidro" : ""}`} style={{ marginBottom: "1em", borderColor: "rgba(212,175,55,0.4)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                    <h6 className="he-titulo he-dourado" style={{ margin: 0 }}><i className="bi bi-calendar2-week"></i> Pagar em diárias</h6>
                    <small style={{ color: "#9aa5b5" }}>Contas que você paga por dia. Cada diária paga entra nas despesas e no Caixa.</small>
                </div>
                <button type="button" className="btn btn-sm btn-he" style={{ whiteSpace: "nowrap" }} onClick={() => setNovo({ nome: "", categoria: "despesa", valor: "", quantidade: "", inicio: hoje, dias: "seg-sab" })}>
                    <i className="bi bi-plus-lg"></i> Nova conta em diárias
                </button>
            </div>

            {paraHoje.length > 0 &&
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "rgba(255, 107, 91, 0.1)", border: "1px solid rgba(255, 107, 91, 0.5)" }}>
                    <div>
                        <div style={{ color: "#9aa5b5", fontSize: "0.8rem" }}>PAGAR ATÉ HOJE{atrasadas.length ? ` · ${atrasadas.length} atrasada${atrasadas.length > 1 ? "s" : ""}` : ""}</div>
                        <strong style={{ color: "#ff6b5b", fontSize: "1.2rem" }}>{real(paraHoje.reduce((s, d) => s + Number(d.valor), 0))}</strong>
                        <span style={{ color: "#9aa5b5", fontSize: "0.85rem" }}> · {paraHoje.length} {paraHoje.length === 1 ? "diária" : "diárias"}</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-he" style={{ whiteSpace: "nowrap" }} onClick={() => setMarcadas(paraHoje.map((d) => d.id))}>Marcar as de hoje</button>
                </div>
            }

            {aviso &&
                <div role="status" style={{ display: "flex", gap: 8, alignItems: "flex-start", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, marginTop: 12, fontSize: "0.9rem", color: "#e8eef5",
                    background: aviso.tipo === "erro" ? "rgba(255, 107, 91, 0.12)" : "rgba(46, 204, 113, 0.12)", border: `1px solid ${aviso.tipo === "erro" ? "#ff6b5b" : "#2ecc71"}` }}>
                    <span><i className={`bi ${aviso.tipo === "erro" ? "bi-exclamation-triangle-fill" : "bi-check-circle-fill"}`} style={{ marginRight: 6 }}></i>{aviso.msg}</span>
                    <button type="button" className="modal-fechar" style={{ minWidth: 32, minHeight: 32, fontSize: "1.3rem" }} onClick={() => setAviso(null)} aria-label="Fechar aviso">×</button>
                </div>
            }

            {carregando ? <div style={{ textAlign: "center", padding: "1.2em" }}><span className="spinner-border spinner-border-sm" style={{ color: "#d4af37" }}></span></div> :
                planos.length === 0 ?
                    <p style={{ color: "#9aa5b5", margin: "12px 0 0", fontSize: "0.9rem" }}>Nenhuma conta em diárias. Cadastre uma e o painel mostra todo dia quanto você tem pra pagar.</p> :
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                        {planos.map((p) => {
                            const quitada = p.abertas.length === 0;
                            const atrasadasP = p.abertas.filter((d) => d.vencimento < hoje).length;
                            const progresso = Math.round((p.pagas.length / (p.diarias.length || 1)) * 100);
                            return (
                                <div key={p.plano} style={{ background: "#0f1e2e", borderRadius: 12, padding: "12px 14px", border: `1px solid ${atrasadasP ? "rgba(255, 107, 91, 0.5)" : "rgba(212, 175, 55, 0.2)"}` }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                                        <div style={{ minWidth: 0 }}>
                                            <strong style={{ color: "#e8eef5" }}>{p.nome}</strong>
                                            {quitada && <span className="badge badge-quitado" style={{ marginLeft: 8 }}>Quitada</span>}
                                            {atrasadasP > 0 && <span className="badge badge-atrasada" style={{ marginLeft: 8 }}>{atrasadasP} atrasada{atrasadasP > 1 ? "s" : ""}</span>}
                                            <div style={{ color: "#9aa5b5", fontSize: "0.82rem" }}>{CATEGORIAS[p.categoria] || "Despesa"} · {real(p.valorDiaria)} por dia · {p.diarias.length} diárias</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ color: "#9aa5b5", fontSize: "0.75rem" }}>{quitada ? "PAGO" : "FALTA PAGAR"}</div>
                                            <strong style={{ color: quitada ? "#2ecc71" : "#ff6b5b" }}>{real(quitada ? p.pago : p.faltam)}</strong>
                                        </div>
                                    </div>
                                    <div style={{ height: 6, background: "#1b2b40", borderRadius: 4, overflow: "hidden", margin: "10px 0 6px" }}>
                                        <div style={{ height: "100%", width: `${progresso}%`, background: "linear-gradient(90deg,#d4af37,#2ecc71)" }}></div>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "0.82rem", color: "#9aa5b5" }}>
                                        <span>Pagou {p.pagas.length} de {p.diarias.length} ({real(p.pago)}){p.abertas[0] ? ` · próxima ${dataBR(p.abertas[0].vencimento)}` : ""}</span>
                                        <span style={{ display: "flex", gap: 8 }}>
                                            <button type="button" className="btn btn-sm btn-outline-he" style={{ whiteSpace: "nowrap" }} onClick={() => alternarAberto(p.plano)}>
                                                {aberto === p.plano ? "Fechar" : "Ver diárias"}
                                            </button>
                                            {!quitada && <button type="button" className="btn btn-sm btn-outline-danger" title="Excluir as diárias em aberto" aria-label={`Excluir as diárias em aberto de ${p.nome}`} onClick={() => handleExcluirPlano(p)}><i className="bi bi-trash"></i></button>}
                                        </span>
                                    </div>

                                    {aberto === p.plano &&
                                        <div className="tabela-rolavel rolagem-so-desktop" style={{ marginTop: 10 }}>
                                            <table className="table table-striped table-hover tabela-responsiva tabela-mini">
                                                <thead>
                                                    <tr><th style={{ whiteSpace: "nowrap" }}>Nº</th><th>Vencimento</th><th>Valor</th><th>Status</th><th>Pagamento</th><th style={{ width: 110 }}></th></tr>
                                                </thead>
                                                <tbody>
                                                    {p.diarias.map((d) => {
                                                        const atrasada = d.status === "aberta" && d.vencimento < hoje;
                                                        return (
                                                            <tr key={d.id}>
                                                                <td className="principal" data-rotulo="Diária">
                                                                    {d.status === "aberta" ?
                                                                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer" }}>
                                                                            <input type="checkbox" className="form-check-input" style={{ width: 20, height: 20, margin: 0 }} aria-label={`Marcar diária ${d.n}`}
                                                                                checked={marcadas.includes(d.id)} onChange={() => alternarMarcada(d.id)} />
                                                                            {d.n}
                                                                        </label>
                                                                        : d.n}
                                                                </td>
                                                                <td data-rotulo="Vencimento" style={{ color: atrasada ? "#ff6b5b" : undefined }}>{dataBR(d.vencimento)}</td>
                                                                <td className="destaque" data-rotulo="Valor">{real(d.valor)}</td>
                                                                <td data-rotulo="Status">
                                                                    <span className={`badge ${d.status === "paga" ? "badge-paga" : atrasada ? "badge-atrasada" : "badge-pendente"}`}>
                                                                        {d.status === "paga" ? "Paga" : atrasada ? "Atrasada" : d.vencimento === hoje ? "Hoje" : "A pagar"}
                                                                    </span>
                                                                </td>
                                                                <td data-rotulo="Pagamento" style={{ fontSize: "0.85em", color: "#9aa5b5" }}>
                                                                    {d.status === "paga" ? `${dataBR(d.dataPagamento)} · ${real(d.valorPago)}` : "—"}
                                                                </td>
                                                                <td className="acoes">
                                                                    <div className="acoes-linha uma-coluna">
                                                                        {d.status === "aberta" &&
                                                                            <button type="button" className="btn btn-sm btn-he" style={{ whiteSpace: "nowrap" }} onClick={() => abrirPagamento([d.id])}>
                                                                                <i className="bi bi-cash"></i> Pagar
                                                                            </button>}
                                                                        {d.status === "paga" &&
                                                                            <button type="button" className="btn btn-sm btn-outline-danger" title="Desfazer pagamento" onClick={() => handleEstornar(d)}>
                                                                                <i className="bi bi-arrow-counterclockwise"></i><span className="so-mobile-inline"> Desfazer</span>
                                                                            </button>}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
            }

            {selecionadas.length > 0 && !pagando &&
                <>
                    <div style={{ height: 150 }}></div>
                    <div role="region" aria-label="Diárias marcadas para pagar" style={barraLote}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ color: "#9aa5b5", fontSize: "0.8rem" }}>{selecionadas.length} {selecionadas.length === 1 ? "diária marcada" : "diárias marcadas"}</div>
                                <div style={{ color: "#d4af37", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1.2 }}>{real(totalSelecionado)}</div>
                            </div>
                            <button type="button" className="modal-fechar" onClick={() => setMarcadas([])} aria-label="Desmarcar todas">×</button>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button type="button" className="btn btn-he" style={botaoCheio} onClick={() => abrirPagamento(selecionadas.map((d) => d.id))}>
                                <i className="bi bi-cash-coin"></i> Pagar {selecionadas.length === 1 ? "a diária" : `as ${selecionadas.length}`}
                            </button>
                        </div>
                    </div>
                </>
            }

            {novo &&
                <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget && !criando) setNovo(null); }}>
                    <form onSubmit={handleCriar} className="modal-caixa form-mobile">
                        <div className="modal-cabecalho">
                            <div>
                                <p className="modal-titulo"><i className="bi bi-calendar2-week"></i> Nova conta em diárias</p>
                                <p className="modal-subtitulo">O painel cria uma diária pra cada dia e mostra quanto pagar.</p>
                            </div>
                            <button type="button" className="modal-fechar" disabled={!!criando} onClick={() => setNovo(null)} aria-label="Fechar">×</button>
                        </div>
                        <div className="modal-corpo">
                            <div className="campo-form">
                                <label className="form-label">Pra quem / o quê</label>
                                <input className="form-control" autoFocus required maxLength={80} placeholder="Ex: Empréstimo João, Fornecedor X" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
                            </div>
                            <div className="campo-form">
                                <label className="form-label">Categoria</label>
                                <select className="form-select" value={novo.categoria} onChange={(e) => setNovo({ ...novo, categoria: e.target.value })}>
                                    {Object.entries(CATEGORIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div className="campo-form">
                                    <label className="form-label">Valor da diária</label>
                                    <input className="form-control" type="number" step="0.01" min="0.01" inputMode="decimal" required placeholder="0,00" value={novo.valor} onChange={(e) => setNovo({ ...novo, valor: e.target.value })} />
                                </div>
                                <div className="campo-form">
                                    <label className="form-label">Quantas diárias</label>
                                    <input className="form-control" type="number" step="1" min="1" max="400" inputMode="numeric" required placeholder="20" value={novo.quantidade} onChange={(e) => setNovo({ ...novo, quantidade: e.target.value })} />
                                </div>
                            </div>
                            <div className="campo-form">
                                <label className="form-label">Primeira diária</label>
                                <input className="form-control" type="date" required value={novo.inicio} onChange={(e) => setNovo({ ...novo, inicio: e.target.value })} />
                            </div>
                            <div className="campo-form">
                                <label className="form-label">Dias de pagamento</label>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    {[["seg-sab", "Segunda a sábado"], ["todos", "Todos os dias"]].map(([v, r]) => (
                                        <button key={v} type="button" aria-pressed={novo.dias === v} className={`btn ${novo.dias === v ? "btn-he" : "btn-outline-he"}`}
                                            style={{ whiteSpace: "nowrap", minHeight: 44, padding: "8px 4px", fontSize: "0.9rem", fontWeight: 700 }} onClick={() => setNovo({ ...novo, dias: v })}>{r}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {datasNovo.length > 0 && Number(novo.valor) > 0 &&
                            <div className="modal-valor" style={{ marginTop: 14, marginBottom: 0 }}>
                                <span>{datasNovo.length} × {real(novo.valor)}<br />{dataBR(datasNovo[0])} a {dataBR(datasNovo[datasNovo.length - 1])}</span>
                                <strong className="valor-dinheiro">{real(datasNovo.length * Number(novo.valor))}</strong>
                            </div>
                        }
                        <div className="modal-acoes">
                            <button type="submit" className="btn btn-he" style={{ whiteSpace: "nowrap" }} disabled={!!criando}>
                                {criando ? <><span className="spinner-border spinner-border-sm"></span> Criando {criando.feitas} de {criando.total}…</> : <><i className="bi bi-check-lg"></i> Cadastrar</>}
                            </button>
                            <button type="button" className="btn btn-outline-he" disabled={!!criando} onClick={() => setNovo(null)}>Cancelar</button>
                        </div>
                    </form>
                </div>
            }

            {pagando &&
                <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget && !pagando.enviando) setPagando(null); }}>
                    <form onSubmit={handlePagar} className="modal-caixa form-mobile">
                        {(() => {
                            const lista = todasAbertas.filter((d) => pagando.ids.includes(d.id));
                            const uma = lista.length === 1;
                            const total = uma && Number(pagando.valor) > 0 ? Number(pagando.valor) : lista.reduce((s, d) => s + Number(d.valor), 0);
                            return (
                                <>
                                    <div className="modal-cabecalho">
                                        <div>
                                            <p className="modal-titulo"><i className="bi bi-cash-coin"></i> Pagar {uma ? `diária ${lista[0]?.n}` : `${lista.length} diárias`}</p>
                                            <p className="modal-subtitulo">{[...new Set(lista.map((d) => d.pessoa))].join(", ")}</p>
                                        </div>
                                        <button type="button" className="modal-fechar" disabled={pagando.enviando} onClick={() => setPagando(null)} aria-label="Fechar">×</button>
                                    </div>
                                    <div className="modal-valor">
                                        <span>Total pago</span>
                                        <strong className="valor-dinheiro">{real(total)}</strong>
                                    </div>
                                    <div className="modal-corpo">
                                        {uma &&
                                            <div className="campo-form">
                                                <label className="form-label">Valor pago</label>
                                                <input className="form-control" type="number" step="0.01" min="0.01" inputMode="decimal" required disabled={pagando.enviando} value={pagando.valor ?? ""} onChange={(e) => setPagando({ ...pagando, valor: e.target.value })} />
                                            </div>}
                                        <div className="campo-form">
                                            <label className="form-label">Data do pagamento</label>
                                            <input className="form-control" type="date" required disabled={pagando.enviando} value={pagando.dataPagamento} onChange={(e) => setPagando({ ...pagando, dataPagamento: e.target.value })} />
                                        </div>
                                        <div className="campo-form">
                                            <label className="form-label">Forma de pagamento</label>
                                            <select className="form-select" disabled={pagando.enviando} value={pagando.formaPagamento} onChange={(e) => setPagando({ ...pagando, formaPagamento: e.target.value })}>
                                                <option value="pix">PIX</option>
                                                <option value="dinheiro">Dinheiro</option>
                                                <option value="transferencia">Transferência</option>
                                                <option value="boleto">Boleto</option>
                                                <option value="cartao">Cartão</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="modal-acoes">
                                        <button type="submit" className="btn btn-he" style={{ whiteSpace: "nowrap" }} disabled={pagando.enviando || !lista.length}>
                                            {pagando.enviando
                                                ? <><span className="spinner-border spinner-border-sm"></span> Pagando {Math.min((pagando.feitas || 0) + 1, lista.length)} de {lista.length}…</>
                                                : <><i className="bi bi-check-lg"></i> Confirmar</>}
                                        </button>
                                        <button type="button" className="btn btn-outline-he" disabled={pagando.enviando} onClick={() => setPagando(null)}>Cancelar</button>
                                    </div>
                                </>
                            );
                        })()}
                    </form>
                </div>
            }
        </div>
    );
}
